import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDraft } from "../../hooks/useDraft";
import {
  clearFailure,
  useAnswerQueueStore,
  type QueuedAct,
  type RowPost,
  type useAnswerQueue,
} from "../../hooks/useAnswerQueue";
import { DraftRefusedError, SAVE_RETRY_MESSAGE, waitingNodeIds, type Queue } from "../../hooks/useQueues";
import { useSettleGuard } from "../../hooks/useSettleGuard";
import { rootNameOf } from "../../lib/displayName";
import {
  CONFIRM_PICK,
  EDIT_ACT,
  SAVE_ACT,
  SAVED_WORD,
  SAVE_FAILED_LEAD,
  SAVING_WORD,
  type ReviewTreeMeta,
} from "../../lib/reviewLines";
import { stripAnchors, type RootName } from "../../lib/stripAnchors";
import { AnswerBlock, ConfirmYesButton, isOptionText, routingPickOf } from "./AnswerBlock";
import { confirmYesHeldByEdit } from "./QuestionCard";
import { NavFooter, asString, isTypingTarget, pickText, readPicks, type Pick } from "./shared";
import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];
type AnswerQueue = ReturnType<typeof useAnswerQueue>;
type Job = Parameters<AnswerQueue["enqueue"]>[0];

// ---- The shared confirm card (0074) ------------------------------------------
//
// A pass's waiting questions whose only option is the engine's confirm option
// (isConfirmGroupMember, lib/reviewCards.ts) share this one card, one row per
// question: its title, the engine's read, its question and its hypothesis, then
// its own one-tap Yes and Edit. A Yes saves its row's question only, through
// Review's answer queue (hooks/useAnswerQueue.ts): taps are taken at once and
// post one at a time, in tap order; a row reads Saved only after its own post
// succeeded. A saved row offers Edit, and changes only when the founder saves
// new words in its box; nothing returns it to waiting. The card has no heading,
// no count and no key act of its own.

// The page scrolls to its top (the next card's read starts in view). jsdom
// implements no scrolling, so the call is guarded.
export function scrollPageToTop(): void {
  try {
    if (typeof window.scrollTo === "function") window.scrollTo(0, 0);
  } catch {
    /* no scrolling here */
  }
}

// A row as the live queue serves it: its served copy (else the node the card
// mounted with), and whether the queue serves it as saved - an item the queue
// serves that does not wait (`waiting` is waitingNodeIds of that queue, the
// one client read of what waits, which the tab counts and the moves read too).
type ServedRow = { copy: Node; servedSaved: boolean };
function servedRowOf(mounted: Node, served: Queue | undefined, waiting: ReadonlySet<string> | undefined): ServedRow {
  const entry = served?.items.find((n) => n.id === mounted.id);
  return {
    copy: entry ?? mounted,
    servedSaved: entry !== undefined && waiting !== undefined && !waiting.has(entry.id),
  };
}

// A row's confirm option among its served options, and that option's text (its
// value, else its label, verbatim; "" when the row serves none).
function confirmOf(picks: readonly Pick[]): { confirm: Pick | undefined; confirmText: string } {
  const confirm = picks.find((p) => p.pickId === CONFIRM_PICK);
  return { confirm, confirmText: confirm ? pickText(confirm) : "" };
}

// A row's saved answer: its own post's, while that post landed no earlier than
// the served queue was read (a tie keeps the post's own success, since its
// invalidation refetch lands after it); else the served answer, when the queue
// serves the row as saved and the answer is non-blank; else none. So a queue
// read after the post always wins over the kept state.
function savedAnswerOf(post: RowPost | undefined, row: ServedRow, servedAt: number) {
  if (post?.settled !== undefined && (post.settledAt ?? 0) >= servedAt) return post.settled;
  const answer = asString(row.copy.fields?.answer);
  return row.servedSaved && answer.trim() !== "" ? answer : undefined;
}

// The row holds an edit it has not saved: non-blank text that is not the text
// the row keeps - its saved answer, or with none the confirm option's text its
// Yes would post (confirmYesHeldByEdit, the one rule for both).
function holdsUnsavedEdit(draft: string, saved: string | undefined, confirmText: string): boolean {
  return confirmYesHeldByEdit(draft, saved ?? confirmText);
}

// A read no older than a row's last failure serves the row as saved with the
// very answer that failed post carried: the post committed though its response
// was lost, so the failure no longer stands (a newer read wins over the kept
// state, as it does over a kept save). A read serving another answer - the one
// a failed change would have replaced - leaves the failure standing, and so
// does an older read, which cannot say what the post did.
function failureSuperseded(post: RowPost | undefined, row: ServedRow, servedAt: number): boolean {
  return (
    post?.error !== undefined &&
    post.failedAt !== undefined &&
    servedAt >= post.failedAt &&
    row.servedSaved &&
    isOptionText(asString(row.copy.fields?.answer), post.failed ?? "")
  );
}

// A row's last post failed with something other than a refusal (a refusal is
// the page's notice, never a row's line), and no newer read superseded it.
function rowErrorShown(post: RowPost | undefined, row: ServedRow, servedAt: number): boolean {
  return (
    post?.error !== undefined &&
    !(post.error instanceof DraftRefusedError) &&
    !failureSuperseded(post, row, servedAt)
  );
}

// What the card reads of a row when a post lands, kept current by the row: its
// draft, and whether its box was opened by Edit, typed in, and saved by a post
// tapped after it opened.
type RowLive = { draft: string; box: { openedByEdit: boolean; typedIn: boolean; saved: boolean } };

export function ConfirmGroupCard({
  productId,
  nodes,
  served,
  servedAt,
  treeMeta,
  pos,
  nothingWaits,
  onPrev,
  onNext,
  onActNext,
  actsHeld,
  mountedByDataSwap,
  queue,
  rowInView,
}: {
  productId: string;
  // The card's items as Review renders it; read once, at mount.
  nodes: readonly Node[];
  // The live questions queue and when it was read (its dataUpdatedAt).
  served: Queue | undefined;
  servedAt: number;
  treeMeta?: ReviewTreeMeta;
  pos: number;
  nothingWaits: boolean;
  onPrev: () => void;
  // The founder's own forward move (skip →, the right arrow).
  onNext: () => void;
  // The forward move the card's last save causes (an act's move).
  onActNext: () => void;
  // The page's mount gate: holds every Yes and Save, and the boxes' key.
  actsHeld: boolean;
  // 0148 A40: mounted because the anchored card left the queue; the boxes'
  // Cmd/Ctrl+Enter waits for a click inside the card.
  mountedByDataSwap: boolean;
  queue: AnswerQueue;
  // The item a link named (the URL's fragment), or "".
  rowInView: string;
}) {
  // The rows are the members the card mounted with, for the whole mount: rows
  // never come or go under the founder; a changed membership shows on the next
  // mount.
  const [members] = useState(() => nodes);
  const [inView] = useState(rowInView);
  const [keyActsHeld, setKeyActsHeld] = useState(mountedByDataSwap);
  const rootName = rootNameOf(treeMeta);

  // What the move-on reads when a post lands: the latest render's served queue
  // and act-advance, and each row's live state - never what the tap's render
  // closed over.
  const latest = useRef({ served, servedAt, onActNext });
  useLayoutEffect(() => {
    latest.current = { served, servedAt, onActNext };
  });
  const [live] = useState(
    () =>
      new Map<string, RowLive>(
        members.map((m) => [m.id, { draft: "", box: { openedByEdit: false, typedIn: false, saved: false } }]),
      ),
  );
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const movedOn = useRef(false);

  // What waits in the served queue (the one client read), read once per queue.
  const waiting = useMemo(() => (served === undefined ? undefined : waitingNodeIds(served)), [served]);

  // An error line coming or going moves every row below it: each row counts
  // how often its line came or went during this mount, and every act below
  // restarts its settle clock when the count above it changes.
  const errorSig = members
    .map((m) => (rowErrorShown(queue.rows.get(m.id), servedRowOf(m, served, waiting), servedAt) ? "1" : "0"))
    .join("");
  const [errorFlips, setErrorFlips] = useState(() => ({ sig: errorSig, counts: members.map(() => 0) }));
  if (errorFlips.sig !== errorSig) {
    setErrorFlips({
      sig: errorSig,
      counts: errorFlips.counts.map((n, i) => (errorFlips.sig[i] !== errorSig[i] ? n + 1 : n)),
    });
  }

  // Nothing left on the card: every row saved, no post queued or out, no
  // unsaved edit, and no box opened by Edit left neither typed in nor saved.
  // Read live, when a post lands.
  const nothingLeft = (): boolean => {
    const rows = useAnswerQueueStore.getState().rows[productId] ?? {};
    const { served: liveServed, servedAt: liveServedAt } = latest.current;
    const liveWaiting = liveServed === undefined ? undefined : waitingNodeIds(liveServed);
    return members.every((m) => {
      const post = rows[m.id];
      if (post?.pending) return false;
      const servedRow = servedRowOf(m, liveServed, liveWaiting);
      const saved = savedAnswerOf(post, servedRow, liveServedAt);
      if (saved === undefined) return false;
      const row = live.get(m.id);
      if (row === undefined) return true;
      const { confirmText } = confirmOf(readPicks(servedRow.copy.fields?.parsed_picks));
      if (holdsUnsavedEdit(row.draft, saved, confirmText)) return false;
      return !(row.box.openedByEdit && !row.box.typedIn && !row.box.saved);
    });
  };

  // A row's act: queued at once; when its post lands, the row reads it first,
  // then the act that leaves nothing on the card moves on, once, from the top
  // of the page (the next page may open at the window's scroll).
  const post = (job: Job, onLanded: (ok: boolean) => void): void => {
    void queue.enqueue(job).then((ok) => {
      onLanded(ok);
      if (!ok || !mounted.current || movedOn.current || !nothingLeft()) return;
      movedOn.current = true;
      scrollPageToTop();
      latest.current.onActNext();
    });
  };

  // Plain ←/→ outside a text box navigate (never an act); no key saves or
  // confirms from the window.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e)) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft" && pos > 0) {
        e.preventDefault();
        onPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  let above = 0;
  return (
    <div
      data-testid="confirm-group-card"
      className="animate-slide-up"
      onClickCapture={() => setKeyActsHeld(false)}
    >
      {members.map((m, i) => {
        const aboveFlips = above;
        above += errorFlips.counts[i];
        return (
          <ConfirmRow
            key={m.id}
            productId={productId}
            mounted={m}
            first={i === 0}
            served={served}
            waiting={waiting}
            servedAt={servedAt}
            rootName={rootName}
            post={queue.rows.get(m.id)}
            actsHeld={actsHeld}
            keyActsHeld={keyActsHeld}
            aboveFlips={aboveFlips}
            live={live.get(m.id)!}
            onPost={post}
            scrollIntoViewOnMount={i > 0 && inView === m.id}
          />
        );
      })}
      <NavFooter pos={pos} nothingWaits={nothingWaits} onPrev={onPrev} onNext={onNext} />
    </div>
  );
}

function ConfirmRow({
  productId,
  mounted,
  first,
  served,
  waiting,
  servedAt,
  rootName,
  post,
  actsHeld,
  keyActsHeld,
  aboveFlips,
  live,
  onPost,
  scrollIntoViewOnMount,
}: {
  productId: string;
  mounted: Node;
  first: boolean;
  served: Queue | undefined;
  waiting: ReadonlySet<string> | undefined;
  servedAt: number;
  rootName: RootName | undefined;
  post: RowPost | undefined;
  actsHeld: boolean;
  keyActsHeld: boolean;
  aboveFlips: number;
  live: RowLive;
  onPost: (job: Job, onLanded: (ok: boolean) => void) => void;
  scrollIntoViewOnMount: boolean;
}) {
  const id = mounted.id;
  const servedRow = servedRowOf(mounted, served, waiting);
  const copy = servedRow.copy;
  const fields = (copy.fields ?? {}) as Record<string, unknown>;
  const labelId = useId();
  const questionId = useId();

  const title = copy.title ?? "";
  const playback = stripAnchors(asString(fields.playback), rootName);
  const questionText = stripAnchors(asString(fields.question), rootName);
  const hypothesis = stripAnchors(asString(fields.hypothesis), rootName);
  const showLabel = questionText.trim() !== "" && title.trim() !== "" && title !== questionText;
  const headingText = questionText.trim() !== "" ? questionText : title;
  // The row's name: its label when shown, else its question. The row's group,
  // its Yes and its Edit all point at it.
  const nameId = showLabel ? labelId : questionId;

  const picks = readPicks(fields.parsed_picks);
  const { confirm, confirmText } = confirmOf(picks);
  // `commit` writes the draft to this browser at once (useDraft): the answer a
  // tap posts is the row's draft from that tap on, so a card unmounted before
  // the typing-time write never brings an older draft back over it.
  const [draft, setDraft, , commit] = useDraft(productId, id, asString(fields.answer));
  const saved = savedAnswerOf(post, servedRow, servedAt);
  const unsavedEdit = holdsUnsavedEdit(draft, saved, confirmText);
  const pending = post?.pending ?? null;
  const slot = pending !== null ? "saving" : saved !== undefined ? "saved" : "waiting";

  // The box: opened by Edit, or by an unsaved edit (never hidden); once open it
  // stays open for the mount, a successful Save included.
  const [opened, setOpened] = useState(false);
  if (unsavedEdit && !opened) setOpened(true);
  const boxOpen = opened || unsavedEdit;

  // The card reads the row's draft and box when a post lands.
  useLayoutEffect(() => {
    live.draft = draft;
  });

  // Each act takes no tap or key for the settle time after the card mounts,
  // after this row's slot or box changes, and after a row above it gains or
  // loses its error line.
  const settleKey = `${slot}|${boxOpen ? "open" : "closed"}|${aboveFlips}`;
  const editGuard = useSettleGuard({ resetKey: settleKey });

  const rowRef = useRef<HTMLDivElement>(null);
  const actLineRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Every focus the card moves leaves the page where it is (preventScroll):
  // nothing slides under the founder's finger.
  const [focusRequest, setFocusRequest] = useState(0);
  useEffect(() => {
    if (focusRequest > 0) textareaRef.current?.focus({ preventScroll: true });
  }, [focusRequest]);

  // A link to this row: scrolled into view once, after mount (jsdom has no
  // scrollIntoView); no focus moves.
  const scrolled = useRef(false);
  useEffect(() => {
    if (!scrollIntoViewOnMount || scrolled.current) return;
    scrolled.current = true;
    const el = rowRef.current;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A post of this row landed. A box counts as saved by a post tapped after it
  // opened. When the post succeeded while focus is on the control of the act
  // that posted it, focus moves to the row (never to any Yes) without scrolling
  // the page, so a post landing while he reads a lower row moves nothing; with
  // focus anywhere else, the row's textarea included, it stays.
  const landed = (ok: boolean, act: QueuedAct, tappedAfterOpen: boolean) => {
    if (!ok) return;
    if (tappedAfterOpen) live.box.saved = true;
    const el = document.activeElement;
    const onControl =
      act === "yes"
        ? actLineRef.current?.contains(el) === true
        : el instanceof HTMLButtonElement && boxRef.current?.contains(el) === true;
    if (onControl) rowRef.current?.focus({ preventScroll: true });
  };

  // The Yes: the confirm option's text verbatim, with its routing id.
  const yesPosting = pending === "yes";
  const yesGated = actsHeld || pending === "save";
  const onYes = () => {
    if (confirm === undefined || yesPosting || yesGated || unsavedEdit) return;
    const tappedAfterOpen = live.box.openedByEdit;
    commit(confirmText);
    onPost({ questionId: id, answer: confirmText, selectedPick: CONFIRM_PICK, act: "yes" }, (ok) =>
      landed(ok, "yes", tappedAfterOpen),
    );
  };

  // Edit opens the box, with focus in it; it posts nothing, so neither the
  // mount gate nor a pending post holds it.
  const onEdit = () => {
    if (boxOpen) return;
    live.box.openedByEdit = true;
    setOpened(true);
    setFocusRequest((n) => n + 1);
  };

  const onDraftChange = (value: string) => {
    live.draft = value;
    live.box.typedIn = true;
    setDraft(value);
  };

  // The box's Save: the trimmed text, with the routing id of the option it is
  // the text of (routingPickOf). Never a blank answer, never the saved one
  // again (a saved row takes only an unsaved edit), never while a post of the
  // row is out.
  const trimmedDraft = draft.trim();
  const saveDisabled = trimmedDraft === "" || (saved !== undefined && !unsavedEdit) || pending !== null || actsHeld;
  const onSave = () => {
    if (saveDisabled) return;
    commit(trimmedDraft);
    onPost(
      {
        questionId: id,
        answer: trimmedDraft,
        selectedPick: routingPickOf(picks, trimmedDraft) || undefined,
        act: "save",
      },
      (ok) => landed(ok, "save", true),
    );
  };

  // A failure a newer read superseded leaves the row's line at once and the
  // queue's state for good, so a later read never brings it back.
  const errorShown = rowErrorShown(post, servedRow, servedAt);
  const supersededAt = failureSuperseded(post, servedRow, servedAt) ? post?.failedAt : undefined;
  useEffect(() => {
    if (supersededAt !== undefined) clearFailure(productId, id, supersededAt);
  }, [productId, id, supersededAt]);
  const errorMessage = post?.error instanceof Error ? post.error.message : SAVE_RETRY_MESSAGE;

  return (
    <div
      ref={rowRef}
      data-testid="confirm-row"
      data-node-id={id}
      role="group"
      tabIndex={-1}
      aria-labelledby={nameId}
      className={`scroll-mt-16${first ? "" : " mt-8 border-t border-ink-800 pt-8"}`}
    >
      {showLabel && (
        <p id={labelId} className="mb-2 text-[12px] font-mono uppercase tracking-widest text-ink-500">
          {title}
        </p>
      )}
      {playback.trim() !== "" && (
        <p className="mb-3 break-words text-[15px] text-ink-300 leading-relaxed whitespace-pre-wrap">{playback}</p>
      )}
      <h2
        id={questionId}
        className="break-words text-[15px] font-medium text-ink-50 leading-snug whitespace-pre-wrap"
      >
        {headingText}
      </h2>
      {hypothesis.trim() !== "" && (
        <p className="mt-2 break-words text-[13px] text-ink-400 leading-relaxed whitespace-pre-wrap">{hypothesis}</p>
      )}

      {/* The slot: the act line (the Yes, or Saved) and Edit's line under it,
          the same height and places in every state, so no act takes another's
          place. */}
      <div data-testid="row-slot" className="mt-4">
        <div ref={actLineRef} className="flex min-h-11 items-center sm:min-h-9">
          {saved !== undefined ? (
            <span data-testid="row-saved" className="font-mono text-[11px] text-ink-400">
              {SAVED_WORD}
            </span>
          ) : (
            confirm !== undefined && (
              <ConfirmYesButton
                posting={yesPosting}
                held={unsavedEdit}
                gated={yesGated}
                label={stripAnchors(confirm.label)}
                onYes={onYes}
                describedBy={nameId}
                settleKey={settleKey}
              />
            )
          )}
        </div>
        <div className="flex min-h-11 items-center sm:min-h-0">
          <button
            type="button"
            data-testid="row-edit"
            onClick={() => editGuard.onClick(onEdit)}
            onPointerDown={editGuard.onPointerDown}
            onPointerUp={editGuard.onPointerUp}
            onPointerCancel={editGuard.onPointerCancel}
            onKeyDown={editGuard.onKeyDown}
            disabled={boxOpen}
            aria-describedby={nameId}
            className={`min-h-11 text-[13px] text-accent underline-offset-2 hover:underline sm:min-h-0${
              boxOpen ? " invisible" : ""
            }`}
          >
            {EDIT_ACT}
          </button>
        </div>
      </div>

      {saved !== undefined && !isOptionText(saved, confirmText) && !boxOpen && (
        <p
          data-testid="row-saved-answer"
          className="mt-3 border-l-2 border-ink-700 pl-3 text-[14px] text-ink-200 leading-relaxed whitespace-pre-wrap break-words"
        >
          {saved}
        </p>
      )}

      {boxOpen && (
        <div ref={boxRef}>
          <AnswerBlock
            hasOptions={false}
            boxOpen
            draft={draft}
            onDraftChange={onDraftChange}
            pending={pending === "save"}
            textareaRef={textareaRef}
            act={pending === "save" ? SAVING_WORD : SAVE_ACT}
            onAct={onSave}
            actDisabled={saveDisabled}
            submitKeyInside={!keyActsHeld}
            tapTarget
            settleKey={settleKey}
          />
        </div>
      )}

      {errorShown && (
        <p data-testid="row-error" className="mt-4 text-sm text-danger">
          {SAVE_FAILED_LEAD}
          {errorMessage}
        </p>
      )}
    </div>
  );
}
